import { CheckPenIcon, InfoCircleIcon } from '@prophecy/icons';
import { negateVariable, theme } from '@prophecy/theme';
import { Separator } from '@prophecy/ui-v3';
import { Badges } from '@prophecy/ui-v3/Badges';
import { Button } from '@prophecy/ui-v3/Button';
import { Stack } from '@prophecy/ui-v3/Layout/Stack';
import { Tooltip } from '@prophecy/ui-v3/Tooltip';
import { Text } from '@prophecy/ui-v3/Typography/Text';
import styled from 'styled-components';

import { PlanAction } from '../Billing/ProfessionalEdition/types';

export interface PricingTierCardProps {
  /** The title of the pricing tier */
  title: string;
  /** The price of the pricing tier */
  price: string | null;
  /** The period of the pricing tier */
  period?: string;
  /** The subtitle of the pricing tier */
  priceSubtitle?: string;
  /** The description of the pricing tier */
  description: string;
  /** The features of the pricing tier */
  features: React.ReactNode[];
  /** Whether the pricing tier is active */
  isActive?: boolean;
  /** The action of the pricing tier */
  action: PlanAction;
  /** The footer of the pricing tier */
  footer?: string;
  /** The pricing tier that this pricing tier extends from */
  extendsFrom?: string;
}

export const PricingTierCard = ({
  title,
  price,
  priceSubtitle,
  period,
  description,
  features,
  footer,
  action,
  isActive,
  extendsFrom
}: PricingTierCardProps) => {
  return (
    <PricingTierCardWrapper>
      {isActive && (
        <StyledActiveBadge
          variant='pillLight'
          size='s'
          icon={<CheckPenIcon size='xs' type='extended' color={theme.colors.brand600} />}
          iconPlacement='left'>
          <Text level='xs' tone={theme.colors.brand700} weight={theme.fontWeight.semiBold}>
            Current plan
          </Text>
        </StyledActiveBadge>
      )}
      <Stack direction='vertical' align='center' gap={theme.spaces.x12}>
        <Stack direction='vertical' gap={theme.spaces.x4}>
          <Text level='sm' tone={theme.colors.success600} weight={theme.fontWeight.semiBold}>
            {title}
          </Text>
          <Separator />
        </Stack>
        <Stack direction='vertical' gap={theme.spaces.x4} padding={`${theme.spaces.x12} 0`}>
          <Stack direction='horizontal' alignY='baseline' gap={theme.spaces.x4}>
            <PriceText level='xl' weight={theme.fontWeight.semiBold} tone={theme.colors.gray900}>
              {price}
            </PriceText>
            {period && (
              <Text level='sm' tone={theme.colors.gray600} weight={theme.fontWeight.medium}>
                {period}
              </Text>
            )}
          </Stack>
          <Text level='xs' tone={theme.colors.gray600} weight={theme.fontWeight.medium}>
            {priceSubtitle}
          </Text>
        </Stack>
        <Text level='sm' tone={theme.colors.gray500}>
          {description}
        </Text>
      </Stack>
      {action && (
        <Tooltip title={action.disabledMessage}>
          <PricingTierCardActionButton
            variant={action.variant}
            size='xl'
            data-test-id='current-plan-button'
            onClick={action.onClick}
            disabled={Boolean(action.disabledMessage)}
            loading={action.isLoading}>
            <Stack direction='horizontal' align='center' alignY='center' gap={theme.spaces.x6}>
              {/* The check icon marks the active plan's resting state; an actionable button on the
                  active card (e.g. "Renew now" for an expired plan) shows its own label instead */}
              {isActive && !action.onClick && <CheckPenIcon size='s' type={'extended'} />}
              <Text level='md' weight={theme.fontWeight.semiBold}>
                {action.label}
              </Text>
            </Stack>
          </PricingTierCardActionButton>
        </Tooltip>
      )}
      <PricingTierCardFeatureLayout direction='vertical' gap={theme.spaces.x20}>
        <Stack direction='vertical' gap={theme.spaces.x4}>
          <Text level='xs' weight={theme.fontWeight.medium}>
            FEATURES
          </Text>
          {extendsFrom && (
            <Text level='sm' tone={theme.colors.gray600}>
              Everything in {extendsFrom}, plus...
            </Text>
          )}
        </Stack>
        <Stack direction='vertical' gap={theme.spaces.x16}>
          {features.map((feature, index) => (
            <Stack direction='horizontal' gap={theme.spaces.x12} key={index} alignY='center'>
              <CheckIconWrapper>
                <CheckPenIcon size='xs' type='extended' color={theme.colors.brand600} />
              </CheckIconWrapper>
              <Text level='sm' tone={theme.colors.gray600}>
                {feature}
              </Text>
            </Stack>
          ))}
        </Stack>
        {footer && (
          <PricingTierCardFooter direction='horizontal' gap={theme.spaces.x12}>
            <InfoCircleIcon type={'default'} color={theme.colors.gray700} />
            <Text level='sm' tone={theme.colors.gray700} weight={theme.fontWeight.medium}>
              {footer}
            </Text>
          </PricingTierCardFooter>
        )}
      </PricingTierCardFeatureLayout>
    </PricingTierCardWrapper>
  );
};

// Spans the 3 rows (header/price/description, action button, features) of the parent
// grid via subgrid so these sections line up across sibling cards
const PricingTierCardWrapper = styled.div`
  display: grid;
  grid-template-rows: subgrid;
  grid-row: span 3;
  background-color: ${theme.colors.white};
  border-radius: ${theme.radius.x16};
  border: 1px solid ${theme.colors.gray200};
  box-shadow: ${theme.shadows.lg};
  padding: ${theme.spaces.x20};
  position: relative;
`;

const PricingTierCardFeatureLayout = styled(Stack)`
  height: 100%;
`;

const PriceText = styled(Text)`
  font-size: ${theme.display.xs.fontSize};
`;

const PricingTierCardActionButton = styled(Button)`
  justify-self: start;
` as unknown as typeof Button;
const PricingTierCardFooter = styled(Stack)`
  border-radius: ${theme.radius.x8};
  border: 1px solid ${theme.colors.gray300};
  background-color: ${theme.colors.gray25};
  align-self: flex-start;

  &:disabled {
    background-color: ${theme.colors.gray100};
    border-color: ${theme.colors.gray200};
  }
`;

const CheckIconWrapper = styled(Stack)`
  background-color: ${theme.colors.brand50};
  border-radius: ${theme.radius.full};
  padding: ${theme.spaces.x4};
`;

const StyledActiveBadge = styled(Badges)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: ${negateVariable(theme.spaces.x12)};
  border-radius: ${theme.radius.full};
`;
